import React, { createContext, useContext, useMemo, useState } from "react";

// ---------- Languages ----------
export const LANGS = [
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
];

// ---------- Translation dictionary ----------
// Using a flat structure grouped by section. Only PT + EN carry the full UI copy.
// Other languages override the most visible strings; missing keys fall back to EN.
const pt = {
  nav: {
    home: "Início", market: "Mercado Global", comparator: "Comparador",
    simulator: "Simulador", geography: "Geografia", ai: "Assistente IA",
    profile: "Perfil de Risco", glossary: "Glossário", pricing: "Planos",
  },
  hero: {
    kicker: "Visão global. Você no controle.",
    title1: "O que você quer", title2: "decidir hoje?",
    subtitle: "Cotações mundiais, comparações cruzadas e projeções — sem assessor, sem achismo. Para quem ganha bem e prefere decidir sozinho.",
    cta_primary: "Começar agora", cta_secondary: "Falar com o assistente",
    disclaimer: "Conteúdo educacional. Não é aconselhamento financeiro.",
  },
  bento: {
    market_t: "Mercado Global ao vivo", market_d: "17 moedas, cripto e índices mundiais em tempo real.",
    compare_t: "Comparador Cruzado", compare_d: "Coloque casa de Lisboa, Bitcoin e ações lado a lado.",
    sim_t: "Simulador \"E se...\"", sim_d: "Projete quanto rende 5 apartamentos em Xangai em 10 anos.",
    geo_t: "25 Países cobertos", geo_d: "Do Brasil a Singapura — clima de negócios, risco e valorização.",
    ai_t: "Assistente IA", ai_d: "Pergunte em texto ou por voz. Em 8 idiomas.",
    cat_t: "12 classes de ativo", cat_d: "Ações, renda fixa, cripto, imóveis, REITs, ouro, carros e mais.",
    profile_t: "Perfil de Risco", profile_d: "Descubra seu perfil em 5 perguntas — sem cadastro, sem julgamento.",
  },
  weekly: {
    eyebrow: "Decisão da Semana",
    horizon: "Horizonte",
    years: "anos",
    vs: "vs",
    thesis: "Tese",
    metrics: "Métricas-chave",
    risks: "Riscos",
    takeaway: "Takeaway",
    confidence: "Confiança",
    winner: "Vencedor",
    tie: "Empate técnico",
    sources: "Fontes",
    refreshes: "Atualizada toda segunda-feira",
    loading: "Gerando análise da semana…",
  },
  takeaway: "Takeaway",
  common: {
    live: "Ao vivo", updated: "Atualizado", search: "Buscar", lang_label: "Idioma",
    all: "Todos", change: "Variação", price: "Preço", region: "Região",
    explore: "Explorar", send: "Enviar", thinking: "Analisando mercados…",
    add: "Adicionar", remove: "Remover", years: "anos", amount: "Valor (USD)",
    units: "Unidades", simulate: "Simular", compare: "Comparar",
    scenario_base: "Base", scenario_bear: "Pessimista", scenario_bull: "Otimista",
    record: "Gravar", stop: "Parar", play: "Ouvir", save: "Salvar",
    share: "Compartilhar", copied: "Link copiado!", start: "Começar",
    reset: "Refazer", continue: "Continuar", skip: "Pular",
  },
  market: {
    title: "Mercado Global",
    sub: "17 moedas, top criptos e índices de 12 bolsas ao redor do mundo. Converta qualquer valor instantaneamente.",
    tab_fx: "Moedas", tab_crypto: "Cripto", tab_idx: "Índices",
    converter_title: "Conversor universal", converter_amount: "Valor",
    converter_from: "De", converter_to: "Para",
  },
  comparator: {
    title: "Comparador Cruzado",
    sub: "Selecione até 6 ativos e veja lado a lado a projeção, o risco e a liquidez. Compartilhe com 1 clique.",
    add_placeholder: "Adicionar ativo...",
    label_amount: "Valor de entrada (USD)",
    label_years: "Horizonte (anos)",
    share_btn: "Compartilhar comparação",
  },
  simulator: {
    title: "Simulador E se…",
    sub: "Quanto rende seu dinheiro em diferentes cenários, cidades e ativos.",
    city: "Cidade (imóvel)",
  },
  geography: {
    title: "Análise Geográfica",
    sub: "25 países, 5 regiões. Clique em qualquer país para ver clima de investimento, cidades com população, depreciação de carros e projeção a 10 anos.",
    growth: "Crescimento PIB", inflation: "Inflação", risk: "Risco",
    real_estate: "Imóveis (CAGR 5a)", stocks: "Bolsa (retorno 10a)",
    yield: "Yield de aluguel", climate: "Clima de negócios",
    cities: "Cidades", population: "População", car: "Carro (perda em 5 anos)",
  },
  ai: {
    title: "Assistente GOLDORA",
    sub: "Pergunte em texto ou por voz — em 8 idiomas. Powered by Claude Sonnet 4.5 + Whisper.",
    placeholder: "Ex.: Vale a pena comprar um apartamento em Lisboa hoje?",
    examples: [
      "Vale a pena comprar 5 apartamentos em Xangai agora?",
      "Dólar vs. Bitcoin nos próximos 10 anos?",
      "É melhor comprar casa em Miami ou investir em S&P 500?",
      "Como diversificar R$ 2 milhões globalmente?",
    ],
    recording: "Gravando...",
    transcribing: "Transcrevendo...",
    speak: "Falar",
  },
  register: {
    title: "Quase lá — quem é você?",
    sub: "Apenas nome e e-mail. Não pedimos CPF, patrimônio ou cartão. Sua privacidade é o nosso diferencial.",
    name: "Nome completo", email: "E-mail melhor",
    persona_label: "Você é (opcional)",
    personas: [
      { id: "doctor", label: "Médico(a) / Dentista" },
      { id: "athlete", label: "Atleta" },
      { id: "entrepreneur", label: "Empreendedor(a)" },
      { id: "influencer", label: "Influencer / Criador(a)" },
      { id: "professional", label: "Profissional liberal" },
      { id: "other", label: "Outro" },
    ],
    submit: "Entrar na plataforma", privacy: "Não guardamos dados sensíveis. Zero spam.",
  },
  risk: {
    title: "Descubra seu perfil de risco",
    sub: "5 perguntas, 60 segundos. Nenhuma informação de patrimônio. Os dados não saem do seu dispositivo.",
    q1: "Uma queda de 20% no seu investimento te faria:",
    q1_opts: ["Vender tudo imediatamente", "Ficar ansioso e vender parte", "Esperar recuperar", "Aproveitar e comprar mais", "Alavancar para comprar muito mais"],
    q2: "Em quantos anos você espera usar esse dinheiro?",
    q2_opts: ["Menos de 1 ano", "1-3 anos", "3-7 anos", "7-15 anos", "Mais de 15 anos"],
    q3: "Qual seu nível de experiência com investimentos?",
    q3_opts: ["Nenhum", "Básico (poupança, CDB)", "Intermediário (ações, fundos)", "Avançado (derivativos, cripto)", "Profissional"],
    q4: "Qual a maior perda temporária que você aceitaria em um ano?",
    q4_opts: ["0-5%", "5-15%", "15-25%", "25-40%", "40%+"],
    q5: "Seu principal objetivo é:",
    q5_opts: ["Preservar capital", "Renda estável", "Equilíbrio renda e crescimento", "Crescimento agressivo", "Multiplicar o patrimônio rapidamente"],
    result: "Seu perfil", allocation: "Alocação sugerida",
    profile_conservative_desc: "Priorize preservação de capital. Renda fixa global e reserva líquida.",
    profile_moderate_desc: "Exposição controlada ao risco. Mescla de renda fixa, ações blue chip e imóveis.",
    profile_balanced_desc: "Meio termo clássico. Diversificação global ampla é seu aliado.",
    profile_growth_desc: "Foco em crescimento de longo prazo. Ações globais, ETFs, imóveis em mercados emergentes.",
    profile_aggressive_desc: "Alta convicção, alta volatilidade. Growth stocks, cripto e private equity fazem sentido.",
  },
  glossary: {
    title: "Glossário do investidor",
    sub: "Termos explicados em 1 linha. Sem arrogância, sem jargão.",
    search: "Buscar termo...",
  },
  footer: {
    product: "Produto", coverage: "Cobertura",
    currencies: "moedas", countries: "países", asset_classes: "classes de ativos", cryptos: "criptomoedas",
    tagline: "Plataforma global de inteligência de decisão financeira. Para quem prefere decidir sozinho.",
    disclaimer: "Conteúdo puramente educacional e de inteligência de mercado. A GOLDORA não oferece aconselhamento de investimento. Retornos passados não garantem retornos futuros.",
  },
};

const en = {
  nav: {
    home: "Home", market: "Global Market", comparator: "Comparator",
    simulator: "Simulator", geography: "Geography", ai: "AI Assistant",
    profile: "Risk Profile", glossary: "Glossary", pricing: "Pricing",
  },
  hero: {
    kicker: "Global vision. You in control.",
    title1: "What do you want", title2: "to decide today?",
    subtitle: "World quotes, cross-asset comparisons and projections — no advisor, no guessing. For high-earners who prefer to decide on their own.",
    cta_primary: "Get started", cta_secondary: "Talk to the assistant",
    disclaimer: "Educational content. Not financial advice.",
  },
  bento: {
    market_t: "Live Global Market", market_d: "17 currencies, crypto and world indices in real time.",
    compare_t: "Cross-Asset Comparator", compare_d: "Put a Lisbon apartment, Bitcoin and S&P 500 side-by-side.",
    sim_t: "\"What if...\" Simulator", sim_d: "Project what 5 Shanghai apartments yield in 10 years.",
    geo_t: "25 Countries covered", geo_d: "Brazil to Singapore — business climate, risk, growth.",
    ai_t: "AI Assistant", ai_d: "Ask by text or voice. In 8 languages.",
    coach_t: "Strategic Coaching", coach_d: "60s diagnosis + personal AI mentor. Before multiplying, stop losing.",
    cat_t: "12 asset classes", cat_d: "Stocks, bonds, crypto, real estate, REITs, gold, cars and more.",
    profile_t: "Risk Profile", profile_d: "Find your profile in 5 questions — no signup, no judgment.",
  },
  weekly: {
    eyebrow: "Decision of the Week",
    horizon: "Horizon",
    years: "years",
    vs: "vs",
    thesis: "Thesis",
    metrics: "Key metrics",
    risks: "Risks",
    takeaway: "Takeaway",
    confidence: "Confidence",
    winner: "Winner",
    tie: "Technical tie",
    sources: "Sources",
    refreshes: "Refreshed every Monday",
    loading: "Generating this week's analysis…",
  },
  takeaway: "Takeaway",
  common: {
    live: "Live", updated: "Updated", search: "Search", lang_label: "Language",
    all: "All", change: "Change", price: "Price", region: "Region",
    explore: "Explore", send: "Send", thinking: "Analyzing markets…",
    add: "Add", remove: "Remove", years: "years", amount: "Amount (USD)",
    units: "Units", simulate: "Simulate", compare: "Compare",
    scenario_base: "Base", scenario_bear: "Bear", scenario_bull: "Bull",
    record: "Record", stop: "Stop", play: "Play", save: "Save",
    share: "Share", copied: "Link copied!", start: "Start",
    reset: "Restart", continue: "Continue", skip: "Skip",
  },
  market: {
    title: "Global Market",
    sub: "17 currencies, top cryptos and indices from 12 exchanges worldwide. Convert any amount instantly.",
    tab_fx: "Currencies", tab_crypto: "Crypto", tab_idx: "Indices",
    converter_title: "Universal converter", converter_amount: "Amount",
    converter_from: "From", converter_to: "To",
  },
  comparator: {
    title: "Cross-Asset Comparator",
    sub: "Select up to 6 assets and see projections, risk and liquidity side-by-side. Share with one click.",
    add_placeholder: "Add asset...",
    label_amount: "Entry amount (USD)",
    label_years: "Horizon (years)",
    share_btn: "Share comparison",
  },
  simulator: {
    title: "What-if Simulator",
    sub: "How your money grows across scenarios, cities and assets.",
    city: "City (real estate)",
  },
  geography: {
    title: "Geographic Analysis",
    sub: "25 countries, 5 regions. Click any country for investment climate, cities with population, car depreciation and 10-year projection.",
    growth: "GDP growth", inflation: "Inflation", risk: "Risk",
    real_estate: "Real estate (5y CAGR)", stocks: "Stocks (10y return)",
    yield: "Rent yield", climate: "Business climate",
    cities: "Cities", population: "Population", car: "Car (5y loss)",
  },
  ai: {
    title: "GOLDORA Assistant",
    sub: "Ask by text or voice — in 8 languages. Powered by Claude Sonnet 4.5 + Whisper.",
    placeholder: "E.g.: Is it worth buying an apartment in Lisbon today?",
    examples: [
      "Is it worth buying 5 apartments in Shanghai now?",
      "USD vs. Bitcoin for the next 10 years?",
      "Better to buy a house in Miami or invest in S&P 500?",
      "How to diversify $2M globally?",
    ],
    recording: "Recording...",
    transcribing: "Transcribing...",
    speak: "Speak",
  },
  register: {
    title: "Almost there — who are you?",
    sub: "Just name and email. No ID, no net worth, no card. Privacy is our edge.",
    name: "Full name", email: "Best email",
    persona_label: "You are (optional)",
    personas: [
      { id: "doctor", label: "Doctor / Dentist" },
      { id: "athlete", label: "Athlete" },
      { id: "entrepreneur", label: "Entrepreneur" },
      { id: "influencer", label: "Influencer / Creator" },
      { id: "professional", label: "Liberal professional" },
      { id: "other", label: "Other" },
    ],
    submit: "Enter the platform", privacy: "We never store sensitive data. Zero spam.",
  },
  risk: {
    title: "Discover your risk profile",
    sub: "5 questions, 60 seconds. No net-worth data. Nothing leaves your device.",
    q1: "A 20% drop in your investment would make you:",
    q1_opts: ["Sell everything immediately", "Feel anxious and sell part", "Wait for recovery", "Take advantage and buy more", "Leverage to buy even more"],
    q2: "In how many years do you plan to use this money?",
    q2_opts: ["< 1 year", "1-3 years", "3-7 years", "7-15 years", "> 15 years"],
    q3: "What's your investing experience?",
    q3_opts: ["None", "Basic (savings, CDs)", "Intermediate (stocks, funds)", "Advanced (derivatives, crypto)", "Professional"],
    q4: "Max temporary loss you'd accept in a year?",
    q4_opts: ["0-5%", "5-15%", "15-25%", "25-40%", "40%+"],
    q5: "Your main goal is:",
    q5_opts: ["Preserve capital", "Stable income", "Balance income & growth", "Aggressive growth", "Rapid wealth multiplication"],
    result: "Your profile", allocation: "Suggested allocation",
    profile_conservative_desc: "Prioritize capital preservation. Global fixed income and liquid reserves.",
    profile_moderate_desc: "Controlled risk. Fixed income, blue chips and real estate.",
    profile_balanced_desc: "Classic middle-ground. Broad global diversification.",
    profile_growth_desc: "Long-term growth focus. Global equities, ETFs, emerging real estate.",
    profile_aggressive_desc: "High conviction, high volatility. Growth stocks, crypto, private equity fit.",
  },
  glossary: {
    title: "Investor glossary",
    sub: "Terms explained in one line. No arrogance, no jargon.",
    search: "Search term...",
  },
  footer: {
    product: "Product", coverage: "Coverage",
    currencies: "currencies", countries: "countries", asset_classes: "asset classes", cryptos: "cryptos",
    tagline: "Global financial decision intelligence platform. For those who prefer to decide on their own.",
    disclaimer: "Purely educational and market intelligence content. GOLDORA does not provide investment advice. Past returns do not guarantee future returns.",
  },
};

// ---- Lighter overlays for other languages (keep EN fallback for unknown keys) ----
const es = {
  nav: { home: "Inicio", market: "Mercado Global", comparator: "Comparador", simulator: "Simulador", geography: "Geografía", ai: "Asistente IA", profile: "Perfil de Riesgo", glossary: "Glosario", pricing: "Planes" },
  hero: { kicker: "Visión global. Decisiones más claras.", title1: "¿Qué quieres", title2: "decidir hoy?", subtitle: "Cotizaciones mundiales, comparaciones cruzadas y proyecciones — sin asesor, sin adivinanzas.", cta_primary: "Empezar", cta_secondary: "Hablar con el asistente", disclaimer: "Contenido educativo. No es asesoramiento financiero." },
  takeaway: "Conclusión",
  common: { ...en.common, search: "Buscar", send: "Enviar", add: "Añadir", remove: "Quitar", years: "años", record: "Grabar", stop: "Parar", play: "Escuchar", share: "Compartir", copied: "¡Enlace copiado!" },
};
const zh = {
  nav: { home: "首页", market: "全球市场", comparator: "对比器", simulator: "模拟器", geography: "地理分析", ai: "AI 助手", profile: "风险画像", glossary: "术语表" },
  hero: { kicker: "全球视野。更清晰的决策。", title1: "今天您想", title2: "做什么决定？", subtitle: "全球行情、跨资产对比与预测 —— 无顾问，无猜测。", cta_primary: "开始", cta_secondary: "对话助手", disclaimer: "教育内容。非投资建议。" },
  takeaway: "要点",
  common: { ...en.common, search: "搜索", send: "发送", add: "添加", remove: "移除", years: "年", record: "录音", stop: "停止", play: "播放", share: "分享", copied: "链接已复制！" },
};
const de = {
  nav: { home: "Start", market: "Globaler Markt", comparator: "Vergleich", simulator: "Simulator", geography: "Geografie", ai: "KI-Assistent", profile: "Risikoprofil", glossary: "Glossar" },
  hero: { kicker: "Globale Sicht. Klarere Entscheidungen.", title1: "Was möchten Sie", title2: "heute entscheiden?", subtitle: "Weltkurse, Cross-Asset-Vergleiche und Prognosen — ohne Berater, ohne Raten.", cta_primary: "Jetzt starten", cta_secondary: "Assistent öffnen", disclaimer: "Bildungsinhalt. Keine Anlageberatung." },
  takeaway: "Fazit",
  common: { ...en.common, search: "Suchen", send: "Senden", add: "Hinzufügen", remove: "Entfernen", years: "Jahre", record: "Aufnehmen", stop: "Stopp", play: "Abspielen", share: "Teilen", copied: "Link kopiert!" },
};
const fr = {
  nav: { home: "Accueil", market: "Marché Global", comparator: "Comparateur", simulator: "Simulateur", geography: "Géographie", ai: "Assistant IA", profile: "Profil de Risque", glossary: "Glossaire" },
  hero: { kicker: "Vision globale. Décisions plus claires.", title1: "Que voulez-vous", title2: "décider aujourd'hui ?", subtitle: "Cotations mondiales, comparaisons croisées et projections — sans conseiller, sans deviner.", cta_primary: "Commencer", cta_secondary: "Parler à l'assistant", disclaimer: "Contenu éducatif. Pas un conseil financier." },
  takeaway: "À retenir",
  common: { ...en.common, search: "Rechercher", send: "Envoyer", add: "Ajouter", remove: "Retirer", years: "ans", record: "Enregistrer", stop: "Arrêter", play: "Écouter", share: "Partager", copied: "Lien copié !" },
};
const ja = {
  nav: { home: "ホーム", market: "グローバル市場", comparator: "比較", simulator: "シミュレーター", geography: "地理分析", ai: "AI アシスタント", profile: "リスクプロファイル", glossary: "用語集" },
  hero: { kicker: "グローバルな視点。より明確な意思決定。", title1: "今日、何を", title2: "決めたいですか？", subtitle: "世界の相場、クロスアセット比較、予測 ——  アドバイザー不要、推測不要。", cta_primary: "開始", cta_secondary: "アシスタントと話す", disclaimer: "教育コンテンツ。投資助言ではありません。" },
  takeaway: "要点",
  common: { ...en.common, search: "検索", send: "送信", add: "追加", remove: "削除", years: "年", record: "録音", stop: "停止", play: "再生", share: "共有", copied: "リンクをコピーしました！" },
};
const hi = {
  nav: { home: "मुख्य", market: "वैश्विक बाज़ार", comparator: "तुलनाकर्ता", simulator: "सिम्युलेटर", geography: "भूगोल", ai: "AI सहायक", profile: "जोखिम प्रोफ़ाइल", glossary: "शब्दावली" },
  hero: { kicker: "वैश्विक दृष्टि। स्पष्ट निर्णय।", title1: "आज आप क्या", title2: "तय करना चाहते हैं?", subtitle: "विश्व के भाव, क्रॉस-एसेट तुलना और अनुमान — बिना सलाहकार, बिना अनुमान के।", cta_primary: "शुरू करें", cta_secondary: "सहायक से बात करें", disclaimer: "शैक्षणिक सामग्री। वित्तीय सलाह नहीं।" },
  takeaway: "सारांश",
  common: { ...en.common, search: "खोजें", send: "भेजें", add: "जोड़ें", remove: "हटाएं", years: "वर्ष", record: "रिकॉर्ड", stop: "रोकें", play: "सुनें", share: "साझा करें", copied: "लिंक कॉपी हो गया!" },
};

// Deep-merge helper: overlay falls back to en for missing keys.
function merge(base, overlay) {
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const k of Object.keys(overlay || {})) {
    if (overlay[k] !== null && typeof overlay[k] === "object" && !Array.isArray(overlay[k])) {
      out[k] = merge(base?.[k] || {}, overlay[k]);
    } else {
      out[k] = overlay[k];
    }
  }
  return out;
}

const dict = {
  pt: pt,
  en: en,
  es: merge(en, es),
  zh: merge(en, zh),
  de: merge(en, de),
  fr: merge(en, fr),
  ja: merge(en, ja),
  hi: merge(en, hi),
};

const I18nContext = createContext({ lang: "pt", t: dict.pt, setLang: () => {} });

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("intelvest_lang") || "en");
  const value = useMemo(() => {
    return {
      lang,
      t: dict[lang] || dict.en,
      setLang: (l) => {
        localStorage.setItem("intelvest_lang", l);
        setLang(l);
      },
    };
  }, [lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
